/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../types';
import { authService } from '../services/api';
import { queryClient } from '../lib/queryClient';
import { getGlobalAccountFlaggedHandler } from '../contexts/AccountFlaggedContext';
import { resetOrgStore } from './orgStore';
import { safeStorage } from '../utils/safeStorage';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  requestOTP: (email: string, recaptchaToken?: string) => Promise<void>;
  verifyOTP: (email: string, otp: string) => Promise<void>;
  googleLogin: (code: string, redirectUri: string) => Promise<void>;
  githubLogin: (code: string) => Promise<void>;
  microsoftLogin: (code: string, redirectUri: string) => Promise<void>;
  ssoLogin: (code: string, state: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  setShowTour: (show: boolean) => Promise<void>;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: true, // Start as true to prevent flash redirect
      isAuthenticated: false,

      requestOTP: async (email: string, recaptchaToken?: string) => {
        set({ isLoading: true });
        try {
          await authService.requestOTP(email, recaptchaToken);
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      verifyOTP: async (email: string, otp: string) => {
        set({ isLoading: true });
        try {
          const response = await authService.verifyOTP(email, otp);
          const { token, user } = response.data;
          safeStorage.setItem('token', token);
          set({ user, token, isAuthenticated: true, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      googleLogin: async (code: string, redirectUri: string) => {
        set({ isLoading: true });
        try {
          const response = await authService.googleLogin(code, redirectUri);
          const { token, user } = response.data;
          safeStorage.setItem('token', token);
          set({ user, token, isAuthenticated: true, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      githubLogin: async (code: string) => {
        set({ isLoading: true });
        try {
          const response = await authService.githubLogin(code);
          const { token, user } = response.data;
          safeStorage.setItem('token', token);
          set({ user, token, isAuthenticated: true, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      microsoftLogin: async (code: string, redirectUri: string) => {
        set({ isLoading: true });
        try {
          const response = await authService.microsoftLogin(code, redirectUri);
          const { token, user } = response.data;
          safeStorage.setItem('token', token);
          set({ user, token, isAuthenticated: true, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      ssoLogin: async (code: string, state: string) => {
        set({ isLoading: true });
        try {
          const response = await authService.ssoCallback(code, state);
          const { token, user } = response.data;
          safeStorage.setItem('token', token);
          set({ user, token, isAuthenticated: true, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await authService.logout();
        } catch (error) {
          // Continue with logout even if API fails
        }
        safeStorage.removeItem('token');
        // Clear stale org selection so the next user doesn't inherit the
        // previous user's org header (404s if not a member, wrong context if they are).
        safeStorage.removeItem('selected_org_id');
        // Clear React Query cache to prevent data leakage between users
        queryClient.clear();
        resetOrgStore();
        set({ user: null, token: null, isAuthenticated: false });
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...userData } });
        }
      },

      setShowTour: async (show: boolean) => {
        try {
          await authService.updateProfile({ show_tour: show });
          const currentUser = get().user;
          if (currentUser) {
            set({ user: { ...currentUser, show_tour: show } });
          }
        } catch (error) {
          throw error;
        }
      },

      checkAuth: async () => {
        const token = safeStorage.getItem('token');
        if (!token) {
          set({ isAuthenticated: false, user: null, token: null, isLoading: false });
          return;
        }

        try {
          // First validate the token using the public endpoint
          const validateResponse = await authService.validateToken(token);
          if (!validateResponse.data.valid) {
            // Check if the account was deactivated
            const msg = (validateResponse.data.message || '').toLowerCase();
            if (msg.includes('deactivat')) {
              safeStorage.removeItem('token');
              set({ user: null, token: null, isAuthenticated: false, isLoading: false });
              const handler = getGlobalAccountFlaggedHandler();
              if (handler) handler();
              return;
            }
            throw new Error('Token is invalid');
          }

          // Token is valid, get the user profile
          const profileResponse = await authService.getProfile();
          set({ user: profileResponse.data, isAuthenticated: true, token, isLoading: false });
        } catch (error) {
          safeStorage.removeItem('token');
          set({ user: null, token: null, isAuthenticated: false, isLoading: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // After hydration, if we have a token, keep isLoading true until checkAuth completes
        // If no token, set isLoading to false immediately
        if (state) {
          if (!state.token) {
            state.isLoading = false;
          }
        }
      },
    }
  )
);
